import React, { useEffect, useState } from 'react'
import { Box, CardMedia, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'
import CircleIcon from '@mui/icons-material/Circle'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'

import '../styles/main.css'

interface IProps {
  items: string[]
  height?: number | string
  maxWidth: number | string
  parentIndex?: number
  navDotsVisible?: boolean
  navButtonsAlwaysVisible?: boolean
}

const carouselDefaults = {
  navDotsVisible: true,
  navButtonsAlwaysVisible: true,
}

const MediaCarousel: React.FC<IProps> = (props) => {
  const navDotsVisible = props.navDotsVisible === undefined ? carouselDefaults.navDotsVisible : props.navDotsVisible
  const navButtonsAlwaysVisible = props.navButtonsAlwaysVisible === undefined ? carouselDefaults.navButtonsAlwaysVisible : props.navButtonsAlwaysVisible

  const [currentImageIndex, setCurrentImageIndex] = useState<number>(0)
  const [selectedImage, setSelectedImage] = useState<string>(props.items[0])
  const [onHover, setOnHover] = useState(false)
  const [isNext, setIsNext] = useState(true)

  const onNext = () => {
    setIsNext(true)
    setCurrentImageIndex(currentImageIndex + 1)
  }
  const onPrev = () => {
    setIsNext(false)
    setCurrentImageIndex(currentImageIndex - 1)
  }

  const useStyles = makeStyles((theme: Theme) => ({
    icons: {
      display: 'flex',
      justifyContent: 'center',
      color: '#fff',
      cursor: 'pointer',
      margin: '5px',
      filter: 'drop-shadow(0px 0px 3px rgba(0, 0, 0, .65))',
      opacity: onHover ? '0.7' : '0',
      transition: 'all .2s',
      visibility: onHover ? 'visible' : 'hidden',
      '&:hover': {
        color: theme.palette.common.white,
        opacity: 1,
      },
      '&$selectedDot:hover': {
        color: theme.palette.common.white,
        opacity: 1,
      },
    },
    arrowBackground: {
      display: 'flex',
      cursor: 'pointer',
      justifyContent: 'center',
      alignItems: 'center',
      width: '40px',
      height: '100%',
      transition: 'all .1s',
      backgroundColor: onHover ? 'rgba(0, 0, 0, 0.15)' : 'transparent',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.25)',
        '& $icons': {
          opacity: 1,
        },
      },
    },
    disabled: {
      visibility: 'hidden',
    },
    selectedDot: {
      color: theme.palette.common.white,
      opacity: 1,
    },
    currentImage: {
      position: 'relative',
      top: 0,
      left: 0,
    },
    carouselBase: {
      width: props.maxWidth,
      position: 'relative',
      overflow: 'hidden',
      transition: 'all 200ms ease',
      height: props.height,
    },
  }))

  const classes = useStyles()

  useEffect(() => {
    setSelectedImage(props.items[currentImageIndex])
  }, [currentImageIndex, props.items])

  useEffect(() => {
    navButtonsAlwaysVisible && setOnHover(true)
  }, [])

  useEffect(() => {
    props.parentIndex && setCurrentImageIndex(props.parentIndex)
  }, [props.parentIndex])

  return (
    <Box
      className={`${classes.carouselBase} slideshow ${isNext ? 'next' : 'prev'}`}
      onMouseEnter={() => {
        !navButtonsAlwaysVisible && setOnHover(true)
      }}
      onMouseLeave={() => {
        !navButtonsAlwaysVisible && setOnHover(false)
      }}
    >
      <div className='slide'>
        <CardMedia
          component='img' height={props.height} src={selectedImage}
          className={classes.currentImage}
        />
      </div>
      {props.items && props.items.length > 1 &&
        <>
          <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}>
            <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
              {/* Arrows */}
              <Box sx={{
                position: 'absolute',
                top: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                height: '100%',
              }}
              >
                <Box
                  className={`${classes.arrowBackground} ${currentImageIndex === 0 && classes.disabled}`}
                  onClick={onPrev}
                >
                  <ArrowBackIosIcon
                    onClick={onPrev}
                    className={`${classes.icons} ${currentImageIndex === 0 && classes.disabled}`}
                    sx={{ width: '30px' }}
                  />
                </Box>
                <Box
                  className={`${classes.arrowBackground} ${currentImageIndex === props.items.length - 1 && classes.disabled}`}
                  onClick={onNext}
                >
                  <ArrowForwardIosIcon
                    onClick={onNext}
                    className={`${classes.icons} ${currentImageIndex === props.items.length - 1 && classes.disabled}`}
                    sx={{ width: '30px' }}
                  />
                </Box>
              </Box>
              {/* Dots */}
              {navDotsVisible &&
                <Box sx={{
                  position: 'absolute',
                  bottom: 0,
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-end',
                  height: 'auto',
                }}
                >
                  {props.items.map((elem, index) =>
                    <CircleIcon
                      key={index}
                      onClick={() => {
                        setCurrentImageIndex(index)
                      }}
                      className={`${classes.icons} ${index === currentImageIndex && classes.selectedDot}`}
                      sx={{ width: '12px' }}
                    />)}
                </Box>}
            </Box>
          </Box>
        </>}
    </Box>
  )
}

export default MediaCarousel
